// Weekly IVR call synopsis -- browse any week (auto-generated every Sunday
// 11:59pm NZT, see supabase/functions/generate-ivr-weekly-synopsis) via the
// same Prev/Next/date-jump picker as the Reporting tab. Once a week has
// been generated it's just displayed -- no need to regenerate it to read
// it again, so the picker's job is browsing history to see whether things
// are getting better week over week, not re-running anything.
import React, { useEffect, useMemo, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { toast } from '@/components/ui/use-toast';
import { format } from 'date-fns';
import { periodRange, shiftAnchor } from '@/lib/ivrCallLogs';
import {
  loadSynopsisForWeek,
  loadSynopsisSuggestions,
  updateSuggestion,
  generateSynopsisNow,
  type IvrWeeklySynopsis,
  type IvrSynopsisSuggestion,
  type SuggestionStatus,
} from '@/lib/ivrWeeklySynopsis';
import { buildSynopsisPdfHtml, printSynopsis } from '@/lib/ivrSynopsisPdf';
import {
  ChevronLeft,
  ChevronRight,
  RefreshCw,
  Loader2,
  PhoneForwarded,
  PhoneOff,
  CheckCircle2,
  MapPin,
  Route,
  FileDown,
  type LucideIcon,
} from 'lucide-react';

const DEFAULT_TENANT_ID = '1225f4cb-43ae-463d-a6b0-53f721362dbf';

const STATUS_OPTIONS: { value: SuggestionStatus; label: string }[] = [
  { value: 'open', label: 'Open' },
  { value: 'in_progress', label: 'In Progress' },
  { value: 'done', label: 'Done' },
  { value: 'dismissed', label: 'Dismissed' },
];

const dateInputValue = (d: Date) => format(d, 'yyyy-MM-dd');

// The prompt constrains body_html to plain <p>/<ul>/<li>, and this is the
// tenant's own OpenAI output (not third-party input) -- still strip any
// <script> as cheap defense in depth before rendering.
const sanitizeHtml = (html: string) => html.replace(/<script[\s\S]*?<\/script>/gi, '');

const IvrSynopsisTab: React.FC = () => {
  const [anchor, setAnchor] = useState<Date>(new Date());
  const [synopsis, setSynopsis] = useState<IvrWeeklySynopsis | null>(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [suggestions, setSuggestions] = useState<IvrSynopsisSuggestion[]>([]);
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  const [savingId, setSavingId] = useState<string | null>(null);

  const range = useMemo(() => periodRange('week', anchor), [anchor]);
  const weekStartStr = useMemo(() => dateInputValue(range.start), [range]);

  const load = async () => {
    setLoading(true);
    const s = await loadSynopsisForWeek(DEFAULT_TENANT_ID, weekStartStr);
    setSynopsis(s);
    setLoading(false);
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [weekStartStr]);

  useEffect(() => {
    if (!synopsis || synopsis.status !== 'completed') {
      setSuggestions([]);
      return;
    }
    setLoadingSuggestions(true);
    loadSynopsisSuggestions(synopsis.id).then((s) => {
      setSuggestions(s);
      setDrafts(Object.fromEntries(s.map((x) => [x.id, x.admin_note || ''])));
      setLoadingSuggestions(false);
    });
  }, [synopsis]);

  const jumpToDate = (value: string) => {
    if (!value) return;
    const [y, m, d] = value.split('-').map(Number);
    if (!y || !m || !d) return;
    setAnchor(new Date(y, m - 1, d));
  };

  const handleGenerate = async () => {
    setGenerating(true);
    try {
      await generateSynopsisNow(DEFAULT_TENANT_ID, weekStartStr);
      toast({ title: 'Synopsis generated' });
      await load();
    } catch (e) {
      toast({ title: 'Error', description: e instanceof Error ? e.message : String(e), variant: 'destructive' });
    }
    setGenerating(false);
  };

  const handleSaveSuggestion = async (s: IvrSynopsisSuggestion, status?: SuggestionStatus) => {
    setSavingId(s.id);
    try {
      const nextNote = drafts[s.id] ?? s.admin_note;
      const nextStatus = status ?? s.status;
      await updateSuggestion(s.id, { admin_note: nextNote, status: nextStatus });
      setSuggestions((prev) => prev.map((x) => (x.id === s.id ? { ...x, admin_note: nextNote, status: nextStatus } : x)));
      toast({ title: 'Saved' });
    } catch (e) {
      toast({ title: 'Error', description: e instanceof Error ? e.message : String(e), variant: 'destructive' });
    }
    setSavingId(null);
  };

  const isFutureWeek = range.start.getTime() > Date.now();

  const handleSavePdf = () => {
    if (!synopsis || synopsis.status !== 'completed') return;
    printSynopsis(buildSynopsisPdfHtml(synopsis, suggestions));
  };

  return (
    <Card className="shadow-lg bg-card border border-border">
      <CardHeader className="flex flex-row items-center justify-between space-y-0">
        <CardTitle className="text-xl text-primary flex items-center gap-2">
          <Route className="w-5 h-5" />
          Synopsis
        </CardTitle>
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={handleSavePdf}
            disabled={loading || !synopsis || synopsis.status !== 'completed'}
            title="Save this week's synopsis as a PDF"
          >
            <FileDown className="w-4 h-4 mr-2" />
            Save PDF
          </Button>
          <Button variant="outline" size="sm" onClick={load} disabled={loading}>
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-6">
        <p className="text-sm text-muted-foreground">
          Generated automatically every Sunday 11:59pm from that week's call transcripts. Browse past weeks with
          the picker below to see whether things are getting better over time -- a sample is read, not every
          call, so address-trouble counts are a solid estimate, not an exact census.
        </p>

        <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-border bg-background/40 p-3">
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" className="h-7 w-7 p-0" onClick={() => setAnchor((a) => shiftAnchor('week', a, -1))}>
              <ChevronLeft className="w-3.5 h-3.5" />
            </Button>
            <span className="text-sm font-medium text-foreground min-w-[12rem] text-center">{range.label}</span>
            <Button variant="outline" size="sm" className="h-7 w-7 p-0" onClick={() => setAnchor((a) => shiftAnchor('week', a, 1))}>
              <ChevronRight className="w-3.5 h-3.5" />
            </Button>
            <Input type="date" value={dateInputValue(anchor)} onChange={(e) => jumpToDate(e.target.value)} className="dark-input h-7 w-[9.5rem] text-xs" />
          </div>
        </div>

        {loading ? (
          <p className="text-sm text-muted-foreground">Loading…</p>
        ) : isFutureWeek ? (
          <p className="text-sm text-muted-foreground">This week hasn't happened yet.</p>
        ) : !synopsis ? (
          <div className="rounded-lg border border-dashed border-border py-8 text-center space-y-3">
            <p className="text-sm text-muted-foreground">No synopsis generated for this week yet.</p>
            <Button size="sm" onClick={handleGenerate} disabled={generating}>
              {generating ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5 mr-1" />}
              {generating ? 'Generating…' : 'Generate Synopsis'}
            </Button>
          </div>
        ) : synopsis.status === 'running' ? (
          <p className="text-sm text-muted-foreground flex items-center gap-2">
            <Loader2 className="w-4 h-4 animate-spin" /> Generating this week's synopsis…
          </p>
        ) : synopsis.status === 'failed' ? (
          <div className="rounded-lg border border-red-900/40 bg-red-950/20 p-4 space-y-3">
            <p className="text-sm text-red-300">Generation failed: {synopsis.error_message || 'Unknown error'}</p>
            <Button size="sm" onClick={handleGenerate} disabled={generating}>
              {generating ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5 mr-1" />}
              {generating ? 'Retrying…' : 'Retry'}
            </Button>
          </div>
        ) : (
          <div className="space-y-6">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              <StatTile label="Transferred" value={synopsis.transferred_count} total={synopsis.calls_total} tone="critical" icon={PhoneForwarded} />
              <StatTile label="No booking attempt" value={synopsis.zero_attempt_count} total={synopsis.calls_total} tone="warn" icon={PhoneOff} />
              <StatTile label="Completed" value={synopsis.completed_count} total={synopsis.calls_total} tone="good" icon={CheckCircle2} />
              <StatTile
                label="Address trouble"
                value={(synopsis.address_trouble_pickup_count ?? 0) + (synopsis.address_trouble_destination_count ?? 0)}
                total={synopsis.calls_total}
                tone="accent"
                icon={MapPin}
                hint={`${synopsis.address_trouble_pickup_count ?? 0} pickup, ${synopsis.address_trouble_destination_count ?? 0} destination`}
              />
            </div>
            <p className="text-xs text-muted-foreground">
              Read {synopsis.calls_read} of {synopsis.calls_total} calls this week (every transfer, every no-attempt call, plus a sample of the rest).
            </p>

            {synopsis.sections.map((sec, i) => (
              <div key={i}>
                <h4 className="font-semibold text-foreground mb-1.5">{sec.heading}</h4>
                <div
                  className="text-sm text-muted-foreground [&_p]:mb-2 [&_ul]:list-disc [&_ul]:pl-5 [&_li]:mb-1"
                  dangerouslySetInnerHTML={{ __html: sanitizeHtml(sec.body_html) }}
                />
              </div>
            ))}

            {synopsis.transfer_reasons.length > 0 && (
              <div>
                <h4 className="font-semibold text-foreground mb-2">Transfer reasons</h4>
                <div className="rounded-lg border border-border overflow-hidden">
                  {synopsis.transfer_reasons.map((r, i) => {
                    const max = Math.max(...synopsis.transfer_reasons.map((x) => x.count), 1);
                    return (
                      <div key={i} className="relative flex items-center justify-between px-3 py-2 border-b border-border last:border-b-0 bg-background/40">
                        <div className="absolute inset-y-0 left-0 bg-yellow-500/10" style={{ width: `${(r.count / max) * 100}%` }} />
                        <span className="relative text-sm text-foreground">{r.label}</span>
                        <span className="relative text-sm font-semibold text-foreground">{r.count}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            <div>
              <h4 className="font-semibold text-foreground mb-2">Suggestions</h4>
              {loadingSuggestions ? (
                <p className="text-sm text-muted-foreground">Loading…</p>
              ) : suggestions.length === 0 ? (
                <p className="text-sm text-muted-foreground">No suggestions for this week.</p>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {(['train', 'engineer'] as const).map((cat) => (
                    <div key={cat} className="space-y-2">
                      <p className={`text-xs font-semibold uppercase tracking-wide ${cat === 'train' ? 'text-green-400' : 'text-orange-400'}`}>
                        {cat === 'train' ? 'Good fit for bot training' : 'Needs engineering'}
                      </p>
                      {suggestions
                        .filter((s) => s.category === cat)
                        .map((s) => (
                          <div
                            key={s.id}
                            className={`rounded-lg border p-3 space-y-2 ${
                              cat === 'train' ? 'border-green-900/30 bg-green-950/10' : 'border-orange-900/30 bg-orange-950/10'
                            }`}
                          >
                            <p className="text-sm text-foreground">{s.suggestion}</p>
                            <Textarea
                              value={drafts[s.id] ?? ''}
                              onChange={(e) => setDrafts((prev) => ({ ...prev, [s.id]: e.target.value }))}
                              placeholder="Add a note (e.g. assigned to, already fixed)…"
                              className="text-xs min-h-[50px] dark-input"
                            />
                            <div className="flex items-center justify-between gap-2">
                              <select
                                value={s.status}
                                onChange={(e) => handleSaveSuggestion(s, e.target.value as SuggestionStatus)}
                                className="h-8 rounded-md border border-input bg-background px-2 text-xs text-foreground"
                              >
                                {STATUS_OPTIONS.map((o) => (
                                  <option key={o.value} value={o.value}>
                                    {o.label}
                                  </option>
                                ))}
                              </select>
                              <Button size="sm" variant="outline" className="h-8" disabled={savingId === s.id} onClick={() => handleSaveSuggestion(s)}>
                                {savingId === s.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : 'Save Note'}
                              </Button>
                            </div>
                          </div>
                        ))}
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="pt-2 border-t border-border">
              <button
                type="button"
                onClick={handleGenerate}
                disabled={generating}
                className="text-xs text-muted-foreground hover:text-foreground underline underline-offset-2"
              >
                {generating ? 'Regenerating…' : 'Regenerate this week'}
              </button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

const StatTile: React.FC<{
  label: string;
  value: number;
  total: number;
  tone: 'critical' | 'warn' | 'good' | 'accent';
  icon: LucideIcon;
  hint?: string;
}> = ({ label, value, total, tone, icon: Icon, hint }) => {
  const toneClass = { critical: 'text-red-400', warn: 'text-orange-400', good: 'text-green-400', accent: 'text-yellow-500' }[tone];
  const pct = total > 0 ? Math.round((value / total) * 100) : 0;
  return (
    <div className="rounded-lg border border-border bg-background/40 p-3">
      <div className="flex items-center gap-1.5 text-xs text-muted-foreground mb-1">
        <Icon className="w-3.5 h-3.5" /> {label}
      </div>
      <div className={`text-2xl font-bold ${toneClass}`}>{value}</div>
      <div className="text-[11px] text-muted-foreground">
        {pct}% of {total}
        {hint ? ` · ${hint}` : ''}
      </div>
    </div>
  );
};

export default IvrSynopsisTab;
